import { Grid, TextField, CollapsibleCard } from '@atgfr8/atg-ui-tailwind';
import { countryTypes, currencyTypes } from '@common/commonLists';
import { Notification, SelectInput, PhoneInput } from '@components';
import AddressAutoComplete, {
  AddressDetails,
} from '@components/Autocomplete/AddressAutoComplete';
import {
  orderStore,
  updateVendorPropertyAtIndex,
  validateFieldForOrder,
} from '@store/orders';
import { sanitizeStateInput } from '@utils/formUtils';
import { isLTLAdmin } from '@utils/utils';
import { vendorServiceTypes, vendorStatusTypes } from '@views/order/constants';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';

const orderErr = (msg: unknown): string | null =>
  typeof msg === 'string' ? msg : null;

const getVendorNotificationText = () => {
  return `Use this to pay third-party services on your Orders. Be sure that when they invoice, they do so with reference number V-${orderStore.order.id}`;
};

type Props = {
  vendor: VendorContractViewModel;
  index: number;
};

const updateAndValidateVendor = async (
  e: string,
  index: number,
  field: string,
) => {
  updateVendorPropertyAtIndex({
    [field]: e,
  });
  await validateFieldForOrder(
    `vendors.${index}.${field}`,
    `vendors[${index}].${field}`,
  );
};

const VendorBasicInfo = (props: Props & { disableFalveyFields: boolean }) => {
  return (
    <Grid item size={{ sm: 6, md: 4 }} gap={4}>
      <Grid item container spacing={2}>
        <Grid item size={12}>
          <TextField
            label="Vendor (Required)"
            value={props.vendor.name}
            maxLength={50}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'name');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].name`]
            }
            disabled={props.vendor.name === 'Falvey Shippers Insurance'}
            dataTestId="vendor-required-textInput"
          />
        </Grid>
        <Grid item size={12}>
          <SelectInput
            label="Status"
            value={props.vendor.status ?? 'Open'}
            menuItems={vendorStatusTypes.map((item) => ({
              value: item,
              label: item,
            }))}
            onChange={async (val) => {
              await updateAndValidateVendor(val, props.index, 'status');
            }}
            error={orderErr(
              orderStore.orderFormError &&
                orderStore.orderFormError[`vendors[${props.index}].status`],
            )}
            disabled={props.disableFalveyFields}
            dataTestId={formatDataTestId('Vendor status', 'selectInput')}
          />
        </Grid>
        <Grid item size={props.vendor.serviceType === 'Other' ? 8 : 12}>
          <SelectInput
            label="Service Type"
            value={props.vendor.serviceType ?? ''}
            onChange={async (val) => {
              await updateAndValidateVendor(val, props.index, 'serviceType');
            }}
            error={orderErr(
              orderStore.orderFormError &&
                orderStore.orderFormError[
                  `vendors[${props.index}].serviceType`
                ],
            )}
            menuItems={vendorServiceTypes.map((item) => ({
              value: item,
              label: item,
            }))}
            disabled={props.disableFalveyFields}
            dataTestId={formatDataTestId('Vendor service type', 'selectInput')}
          />
        </Grid>
        <Grid
          item
          size={4}
          sx={{
            display: props.vendor.serviceType !== 'Other' ? 'none' : 'block',
          }}
        >
          <TextField
            label="Add text"
            value={props.vendor.serviceTypeText as string}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'serviceTypeText');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[
                `vendors[${props.index}].serviceTypeText`
              ]
            }
            disabled={props.disableFalveyFields}
            dataTestId="add-text-textInput"
          />
        </Grid>
        <Grid item size={12}>
          <TextField
            multiline
            label="Comment/Description (Required)"
            value={props.vendor.description}
            maxLength={2000}
            onChange={async (e) => {
              await updateAndValidateVendor(e, props.index, 'description');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].description`]
            }
            disabled={props.disableFalveyFields}
            dataTestId="comment-description-required-textAreaField"
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const VendorAdditionalInfo = (
  props: Props & { disableFalveyFields: boolean },
) => {
  const itemOnSelect = async (item: AddressDetails) => {
    updateVendorPropertyAtIndex({
      mailingAddress1: item.address1,
      mailingCity: item.city,
      mailingState: item.state,
      mailingZip: item.zipCode,
    });
    await validateFieldForOrder(
      `vendors.${props.index}.mailingAddress1`,
      `vendors[${props.index}].mailingAddress1`,
    );
    await validateFieldForOrder(
      `vendors.${props.index}.mailingCity`,
      `vendors[${props.index}].mailingCity`,
    );
    await validateFieldForOrder(
      `vendors.${props.index}.mailingState`,
      `vendors[${props.index}].mailingState`,
    );
    await validateFieldForOrder(
      `vendors.${props.index}.mailingZip`,
      `vendors[${props.index}].mailingZip`,
    );
  };

  return (
    <Grid item size={{ sm: 6, md: 4 }} spacing={4}>
      <Grid item container spacing={2}>
        <Grid item size={12}>
          <TextField
            label="Contact Name (Required)"
            value={props.vendor.contactName}
            maxLength={50}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'contactName');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].contactName`]
            }
            disabled={props.disableFalveyFields}
            dataTestId="contact-name-required-textInput"
          />
        </Grid>
        <Grid item size={12}>
          <PhoneInput
            label="Phone Number"
            value={props.vendor.contactPhone}
            onChange={async (e) => {
              await updateAndValidateVendor(e, props.index, 'contactPhone');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].contactPhone`]
            }
            disabled={props.disableFalveyFields}
            dataTestId="phone-number-phoneInput"
          />
        </Grid>
        <Grid item size={12}>
          <AddressAutoComplete
            label="Address Line 1"
            placeholder="Enter Address"
            value={props.vendor.mailingAddress1}
            onChange={(e) => {
              updateVendorPropertyAtIndex({
                mailingAddress1: e,
              });
            }}
            onItemSelect={(item) => itemOnSelect(item)}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[
                `vendors[${props.index}].mailingAddress1`
              ]
            }
            disabled={props.disableFalveyFields}
          />
        </Grid>
        <Grid item size={12}>
          <TextField
            label="Address Line 2"
            value={props.vendor.mailingAddress2}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'mailingAddress2');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[
                `vendors[${props.index}].mailingAddress2`
              ]
            }
            disabled={props.disableFalveyFields}
            dataTestId="address-line-2-textInput"
          />
        </Grid>
        <Grid item size={4}>
          <TextField
            label="City"
            value={props.vendor.mailingCity}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'mailingCity');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].mailingCity`]
            }
            disabled={props.disableFalveyFields}
            dataTestId="city-textInput"
          />
        </Grid>
        <Grid item size={4}>
          <TextField
            label="State"
            value={props.vendor.mailingState}
            onChange={async (e: string) => {
              const sanitized = sanitizeStateInput(e);
              if (sanitized === props.vendor.mailingState) return;
              await updateAndValidateVendor(
                sanitized,
                props.index,
                'mailingState',
              );
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].mailingState`]
            }
            disabled={props.disableFalveyFields}
            maxLength={2}
            dataTestId="state-textInput"
          />
        </Grid>
        <Grid item size={4}>
          <TextField
            label="Zip"
            value={props.vendor.mailingZip}
            onChange={async (e: string) => {
              await updateAndValidateVendor(e, props.index, 'mailingZip');
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`vendors[${props.index}].mailingZip`]
            }
            disabled={props.disableFalveyFields}
            dataTestId="zip-textInput"
          />
        </Grid>
        <Grid item size={6}>
          <SelectInput
            label="Country"
            value={props.vendor.countryCode ?? ''}
            onChange={async (val) => {
              await updateAndValidateVendor(val, props.index, 'countryCode');
            }}
            error={orderErr(
              orderStore.orderFormError &&
                orderStore.orderFormError[
                  `vendors[${props.index}].countryCode`
                ],
            )}
            menuItems={countryTypes.map((c) => ({
              label: c.label,
              value: String(c.value),
            }))}
            disabled={props.disableFalveyFields}
            dataTestId={formatDataTestId('Vendor country', 'selectInput')}
          />
        </Grid>
        <Grid item size={6}>
          <SelectInput
            label="Currency"
            value={props.vendor.currencyType ?? ''}
            onChange={async (val) => {
              await updateAndValidateVendor(val, props.index, 'currencyType');
            }}
            error={orderErr(
              orderStore.orderFormError &&
                orderStore.orderFormError[
                  `vendors[${props.index}].currencyType`
                ],
            )}
            menuItems={currencyTypes.map((c) => ({ label: c, value: c }))}
            disabled={props.disableFalveyFields}
            dataTestId={formatDataTestId('Vendor currency', 'selectInput')}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const VendorLegacy = (props: Props) => {
  return (
    <CollapsibleCard
      startTitle="Vendor Details"
      loading={orderStore.loading}
      class="mb-2"
    >
      <Notification type={'info'} text={getVendorNotificationText()} />
      <Grid size={12} container spacing={2} mt={2}>
        <VendorBasicInfo
          disableFalveyFields={
            props.vendor.name === 'Falvey Shippers Insurance' && !isLTLAdmin()
          }
          {...props}
        />
        <VendorAdditionalInfo
          disableFalveyFields={
            props.vendor.name === 'Falvey Shippers Insurance' && !isLTLAdmin()
          }
          {...props}
        />
      </Grid>
    </CollapsibleCard>
  );
};
